import React, { useState, useEffect, ChangeEvent, useMemo, useRef, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Cloud, Phone, Activity, BarChart3, PlusCircle, Brain, ChevronDown, ChevronRight, Coins, GitBranchPlus, MinusCircle, Download, Loader } from 'lucide-react';

// --- DEPENDENCY NOTE ---
// This component requires the jspdf and html2canvas libraries.
// Install them using:
// npm install jspdf html2canvas
// If you are using TypeScript, also install their type definitions:
// npm install --save-dev @types/jspdf @types/html2canvas

// Import them directly
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf'; // Correct way to import jsPDF

// --- CONFIGURATION INTERFACES ---
interface CloudConfig {
  id: string;
  provider: string;
  instance_type: string;
  region: string;
  billing: 'Hourly' | 'Monthly';
  price: number; // USD
  country?: string;
}

interface StorageConfig {
  id: string;
  provider: string;
  region: string;
  price_per_GB: number; // USD
  country?: string;
}

interface TelephonyConfig {
  id: string;
  provider: string;
  region: string;
  direction: 'Inbound' | 'Outbound';
  price_per_minute: number; // USD
  country?: string;
}

interface TTSServiceConfig {
  id: string;
  provider: string;
  language: 'English' | 'Arabic';
  price_per_minute: number; // USD
  country?: string;
}

interface STTServiceConfig {
  id: string;
  provider: string;
  language: 'English' | 'Arabic';
  price_per_minute: number; // USD
  country?: string;
}

interface LLMConfig {
  input_tokens_per_minute: number;
  output_tokens_per_minute: number;
  price_per_million_input: number; // USD
  price_per_million_output: number; // USD
}

interface AppConfig {
  cloud: CloudConfig[];
  storage: StorageConfig[];
  telephony: TelephonyConfig[];
  tts: TTSServiceConfig[];
  stt: STTServiceConfig[];
  llm: LLMConfig;
  countries: string[];
}

// --- FORM DATA & COST INTERFACES ---
type Currency = 'USD' | 'AED' | 'SAR' | 'KWD' | 'BHD';

interface UserSelections {
  selectedCountry: string | null;
  selectedCloudConfigId: string | null;
  cloudHoursPerMonth: number;
  selectedStorageConfigId: string | null;
  storageEstimatedGB: number;
  selectedTTSConfigId: string | null;
  selectedSTTConfigId: string | null;
  selectedInboundTelephonyConfigId: string | null;
  selectedOutboundTelephonyConfigId: string | null;
  totalCallsPerMonth: number;
  avgCallDuration: number;
}

interface AdditionalCharge {
  id: string;
  name: string;
  price: number;
  subCharges?: AdditionalCharge[];
  isExpanded?: boolean;
}

interface Costs {
  totalMinutes: number;
  cloudCost: number;
  storageCost: number;
  deploymentTotalCost: number;
  inboundTelephonyCost: number;
  outboundTelephonyCost: number;
  totalTelephonyCost: number;
  ttsCost: number;
  sttCost: number;
  llmInputCost: number;
  llmOutputCost: number;
  llmTotalCost: number;
  servicesTotalCost: number;
  additionalChargesCost: number;
  totalMonthlyCostUSD: number;
}

// --- DATA & INITIAL VALUES ---
const exampleConfig: AppConfig = {
  countries: ["Global", "UAE", "Bahrain", "Kuwait", "Saudi Arabia", "Qatar", "India"],
  cloud: [
    { id: "aws_t4g_uae", provider: "AWS", instance_type: "t4g.2xlarge", region: "Middle East (UAE)", billing: "Monthly", price: 195.35, country: "UAE" },
    { id: "aws_t4g_bhr", provider: "AWS", instance_type: "t4g.2xlarge", region: "Middle East (Bahrain)", billing: "Monthly", price: 192.06, country: "Bahrain" },
    { id: "gcp_n4_sau", provider: "GCP", instance_type: "n4-standard-8", region: "Dammam (me-central2)", billing: "Monthly", price: 222.67, country: "Saudi Arabia" },
    { id: "azure_f8_qat", provider: "Azure", instance_type: "F8s v2", region: "Qatar Central", billing: "Monthly", price: 308.06, country: "Qatar" },
    { id: "azure_f8_uae_c", provider: "Azure", instance_type: "F8s v2", region: "UAE Central", billing: "Monthly", price: 367.92, country: "UAE" },
    { id: "azure_f8_uae_n", provider: "Azure", instance_type: "F8s v2", region: "UAE North", billing: "Monthly", price: 297.84, country: "UAE" },
    { id: "local_0", provider: "Local/On-Prem", instance_type: "Custom", region: "N/A", billing: "Monthly", price: 0 },
  ],
  storage: [
    { id: "azure_st_qat", provider: "Azure", region: "Qatar Central", price_per_GB: 0.18, country: "Qatar" },
    { id: "azure_st_uae_c", provider: "Azure", region: "UAE Central", price_per_GB: 0.23, country: "UAE" },
    { id: "azure_st_uae_n", provider: "Azure", region: "UAE North", price_per_GB: 0.19, country: "UAE" },
    { id: "s3_bhr", provider: "AWS", region: "Bahrain (me-south-1)", price_per_GB: 0.025, country: "Bahrain" },
    { id: "s3_uae", provider: "AWS", region: "UAE (me-central-1)", price_per_GB: 0.025, country: "UAE" },
    { id: "gcs_sau", provider: "GCS", region: "Dammam (me-central2)", price_per_GB: 0.03, country: "Saudi Arabia" },
    { id: "gcs_qat", provider: "GCS", region: "Doha (me-central1)", price_per_GB: 0.023, country: "Qatar" },
    { id: "local_1", provider: "Local/On-Prem", region: "N/A", price_per_GB: 0 },
  ],
  telephony: [
    { id: "tw_sa_in", provider: "Twilio", region: "SA", direction: "Inbound", price_per_minute: 0.3, country: "Saudi Arabia" },
    { id: "tw_sa_out", provider: "Twilio", region: "SA", direction: "Outbound", price_per_minute: 0.225, country: "Saudi Arabia" },
    { id: "tw_kw_in", provider: "Twilio", region: "KW", direction: "Inbound", price_per_minute: 0.2, country: "Kuwait" },
    { id: "tw_kw_out", provider: "Twilio", region: "KW", direction: "Outbound", price_per_minute: 0.13, country: "Kuwait" },
    { id: "tw_bh_in", provider: "Twilio", region: "BH", direction: "Inbound", price_per_minute: 0.2, country: "Bahrain" },
    { id: "tw_bh_out", provider: "Twilio", region: "BH", direction: "Outbound", price_per_minute: 0.2, country: "Bahrain" },
    { id: "tw_qa_in", provider: "Twilio", region: "QA", direction: "Inbound", price_per_minute: 0.3, country: "Qatar" },
    { id: "tw_qa_out", provider: "Twilio", region: "QA", direction: "Outbound", price_per_minute: 0.3, country: "Qatar" },
    { id: "tw_ae_in", provider: "Twilio", region: "AE", direction: "Inbound", price_per_minute: 0.25, country: "UAE" },
    { id: "tw_ae_out", provider: "Twilio", region: "AE", direction: "Outbound", price_per_minute: 0.245, country: "UAE" },
    { id: "tw_in_in", provider: "Twilio", region: "IN", direction: "Inbound", price_per_minute: 0.27, country: "India" },
    { id: "tw_in_out", provider: "Twilio", region: "IN", direction: "Outbound", price_per_minute: 0.047, country: "India" },
  ],
  tts: [
    { id: "goog_tts_ar", provider: "GoogleTTS", language: "Arabic", price_per_minute: 0.05, country: "Global" },
    { id: "cart_tts_en", provider: "Cartesia", language: "English", price_per_minute: 0.05, country: "Global" },
  ],
  stt: [
    { id: "deep_stt_en", provider: "Deepgram", language: "English", price_per_minute: 0.01, country: "Global" },
    { id: "goog_stt_ar", provider: "GoogleSTT", language: "Arabic", price_per_minute: 0.01, country: "Global" },
  ],
  llm: {
    input_tokens_per_minute: 12500,
    output_tokens_per_minute: 200,
    price_per_million_input: 2.5,
    price_per_million_output: 10,
  },
};

const initialSelections: UserSelections = {
  selectedCountry: "Global", selectedCloudConfigId: null, cloudHoursPerMonth: 730,
  selectedStorageConfigId: null, storageEstimatedGB: 100, selectedTTSConfigId: null,
  selectedSTTConfigId: null, selectedInboundTelephonyConfigId: null,
  selectedOutboundTelephonyConfigId: null, totalCallsPerMonth: 1000, avgCallDuration: 5,
};

const exchangeRates: Record<Currency, number> = { USD: 1, AED: 3.67, SAR: 3.75, KWD: 0.31, BHD: 0.38 };
const PIE_COLORS = ['#2563eb', '#16a34a', '#f97316', '#dc2626', '#9333ea', '#db2777'];

// --- UTILITY & CHILD COMPONENTS ---
const FormField: React.FC<{
  label: string; name: string; value: string | number | null;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  type?: string; options?: { value: string | null; label: string }[];
  placeholder?: string; min?: number; step?: number;
  unit?: string; disabled?: boolean; customClass?: string;
}> = React.memo(({ label, name, value, onChange, type = 'text', options, placeholder, min, step, unit, disabled, customClass }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {unit && <span className="text-xs text-gray-500">({unit})</span>}
    </label>
    {type === 'select' && options ? (
      <select id={name} name={name} value={value === null ? "" : String(value)} onChange={onChange} disabled={disabled}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5 bg-white text-gray-900 ${customClass}`}>
        <option value="">Select...</option>
        {options.map(opt => <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>)}
      </select>
    ) : (
      <input type={type} id={name} name={name} value={value === null ? '' : String(value)} onChange={onChange} placeholder={placeholder} min={min} step={step} disabled={disabled}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5 bg-white text-gray-900 ${customClass}`} />
    )}
  </div>
));

const SectionTitle: React.FC<{ title: string; icon: React.ReactNode }> = ({ title, icon }) => (
  <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b-2 border-slate-200 pb-2">
    {icon} <span className="ml-3">{title}</span>
  </h2>
);

const SummaryItem: React.FC<{ label: string; valueText: string; isSubItem?: boolean }> = ({ label, valueText, isSubItem }) => (
  <div className={`flex justify-between py-1.5 text-sm ${isSubItem ? 'pl-4' : ''}`}>
    <span className="text-gray-600">{label}:</span>
    <span className="font-medium text-gray-800">{valueText}</span>
  </div>
);

const ExpandableSummaryItem: React.FC<{ label: string; valueText: string; isExpanded: boolean; onToggle: () => void; children?: React.ReactNode; }> = ({ label, valueText, isExpanded, onToggle, children }) => (
  <>
    <div className="flex justify-between items-center py-2 cursor-pointer hover:bg-slate-100 rounded-md px-2 -mx-2" onClick={onToggle}>
      <div className="flex items-center">
        {isExpanded ? <ChevronDown size={18} className="mr-2 text-indigo-500" /> : <ChevronRight size={18} className="mr-2 text-gray-500" />}
        <span className="text-gray-800 font-medium">{label}:</span>
      </div>
      <span className="font-semibold text-indigo-700">{valueText}</span>
    </div>
    {isExpanded && <div className="pl-6 border-l-2 border-indigo-200 ml-2.5 mt-1">{children}</div>}
  </>
);

const RenderAdditionalCharge: React.FC<{
  charge: AdditionalCharge; level: number;
  onUpdate: (chargeId: string, field: 'name' | 'price', value: string | number) => void;
  onAddSub: (parentId: string) => void;
  onRemove: (chargeId: string) => void;
  onToggleExpand: (chargeId: string) => void;
}> = React.memo(({ charge, level, onUpdate, onAddSub, onRemove, onToggleExpand }) => (
  <div className={`ml-${level * 4} mb-3 p-3 border ${level > 0 ? 'border-gray-200 bg-slate-50' : 'border-gray-300 bg-white'} rounded-lg shadow-sm`}>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 items-end">
      <FormField label={level === 0 ? 'Category Name' : 'Item Name'} name={`chargeName-${charge.id}`} value={charge.name} onChange={(e) => onUpdate(charge.id, 'name', e.target.value)}
        placeholder={level === 0 ? "e.g., License Fees" : "e.g., SaaS License"} customClass="mb-2 md:mb-0" />
      <FormField label="Price (USD)" name={`chargePrice-${charge.id}`} value={charge.price} onChange={(e) => onUpdate(charge.id, 'price', e.target.value)}
        type="number" min={0} step={0.01} unit="$" customClass="mb-2 md:mb-0" />
      <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0 self-end pb-0 md:pb-3">
        <button onClick={() => onAddSub(charge.id)} title={level === 0 ? "Add Item to this Category" : "Add Sub-Item"} className="text-xs bg-green-100 hover:bg-green-200 text-green-800 py-1.5 px-2 rounded-md flex items-center justify-center grow sm:grow-0"> <GitBranchPlus size={14} className="mr-1" /> {level === 0 ? "Item" : "Sub"}</button>
        <button onClick={() => onToggleExpand(charge.id)} title="Toggle Sub-items" className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 py-1.5 px-2 rounded-md flex items-center justify-center grow sm:grow-0"> {charge.isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />} View</button>
        <button onClick={() => onRemove(charge.id)} title="Delete this Charge/Category" className="text-xs bg-red-100 hover:bg-red-200 text-red-800 py-1.5 px-2 rounded-md flex items-center justify-center grow sm:grow-0"> <MinusCircle size={14} className="mr-1" />Del</button>
      </div>
    </div>
    {charge.isExpanded && charge.subCharges?.length > 0 && (
      <div className="mt-3 pt-3 pl-4 border-l-2 border-dashed border-gray-400">
        <h4 className="text-xs text-gray-600 mb-2 font-medium">Items in: <span className="font-bold">{charge.name || "Unnamed"}</span></h4>
        {charge.subCharges.map(sub => <RenderAdditionalCharge key={sub.id} charge={sub} level={level + 1} onUpdate={onUpdate} onAddSub={onAddSub} onRemove={onRemove} onToggleExpand={onToggleExpand} />)}
      </div>
    )}
  </div>
));

// Defined outside useCallback to avoid 'missing dependency' warning if used within another useCallback
// This function does not depend on component state or props, so it's stable.
const removeChargeById = (charges: AdditionalCharge[], idToRemove: string): AdditionalCharge[] => {
  const updatedCharges = charges.filter(charge => charge.id !== idToRemove);
  if (updatedCharges.length < charges.length) return updatedCharges; // Found and removed at this level
  return charges.map(charge => {
    if (charge.subCharges) return { ...charge, subCharges: removeChargeById(charge.subCharges, idToRemove) };
    return charge;
  });
};

// Defined outside useCallback to avoid 'missing dependency' warning if used within another useCallback
// This function does not depend on component state or props, so it's stable.
const updateChargeRecursively = (charges: AdditionalCharge[], targetId: string, action: 'add_sub' | 'update_field' | 'toggle_expand_form', data: any): AdditionalCharge[] => {
  return charges.map(charge => {
    if (charge.id === targetId) {
      switch (action) {
        case 'add_sub': return { ...charge, isExpanded: true, subCharges: [...(charge.subCharges || []), { id: Date.now().toString(), name: 'New Item', price: 0, subCharges: [], isExpanded: false }] };
        case 'update_field': return { ...charge, ...data };
        case 'toggle_expand_form': return { ...charge, isExpanded: !charge.isExpanded };
        default: return charge;
      }
    }
    if (charge.subCharges) return { ...charge, subCharges: updateChargeRecursively(charge.subCharges, targetId, action, data) };
    return charge;
  });
};


// --- MAIN APP COMPONENT ---
const App: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [config] = useState<AppConfig>(exampleConfig);
  const [selections, setSelections] = useState<UserSelections>(initialSelections);
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharge[]>([]);
  const [costs, setCosts] = useState<Costs>({
    totalMinutes: 0, cloudCost: 0, storageCost: 0, deploymentTotalCost: 0,
    inboundTelephonyCost: 0, outboundTelephonyCost: 0, totalTelephonyCost: 0,
    ttsCost: 0, sttCost: 0, llmInputCost: 0, llmOutputCost: 0, llmTotalCost: 0,
    servicesTotalCost: 0, additionalChargesCost: 0, totalMonthlyCostUSD: 0,
  });
  const [displayCurrency, setDisplayCurrency] = useState<Currency>('USD');
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // UI Expansion States
  const [isDeploymentExpanded, setIsDeploymentExpanded] = useState(true);
  const [isServicesExpanded, setIsServicesExpanded] = useState(false);
  const [isTelephonyExpanded, setIsTelephonyExpanded] = useState(false);
  const [isAdditionalChargesExpanded, setIsAdditionalChargesExpanded] = useState(true);
  const [expandedCustomChargesSummary, setExpandedCustomChargesSummary] = useState<Record<string, boolean>>({});

  // Ref for the chart to capture it as an image
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let processedValue: string | number | null = value;

    if (value === "null" || value === "") processedValue = null;
    else if (type === 'number' && value !== '') processedValue = parseFloat(value);

    setSelections(prev => ({ ...prev, [name]: processedValue as any }));
  };

  // --- RECURSIVE LOGIC FOR ADDITIONAL CHARGES ---
  // NOTE: All handler functions are wrapped in `useCallback` and child components in `React.memo`.
  // This is a crucial performance optimization that prevents the entire list of inputs from
  // re-rendering and losing focus each time a user types a single character.

  const calculateRecursiveChargeCost = useCallback((charges: AdditionalCharge[]): number => charges.reduce((total, charge) => total + (charge.price || 0) + (charge.subCharges ? calculateRecursiveChargeCost(charge.subCharges) : 0), 0), []);

  const handleRemoveCharge = useCallback((chargeId: string) => {
    setAdditionalCharges(prev => removeChargeById(prev, chargeId));
  }, []); // `removeChargeById` is stable (defined outside component)

  const handleAddTopLevelCharge = useCallback(() => setAdditionalCharges(prev => [...prev, { id: Date.now().toString(), name: 'New Category', price: 0, subCharges: [], isExpanded: true }]), []);

  const handleAddSubCharge = useCallback((parentId: string) => setAdditionalCharges(prev => updateChargeRecursively(prev, parentId, 'add_sub', null)), []); // `updateChargeRecursively` is stable

  const handleUpdateChargeField = useCallback((chargeId: string, field: 'name' | 'price', value: string | number) => {
    const updatePayload = field === 'price' ? { price: parseFloat(value as string) || 0 } : { name: value as string };
    setAdditionalCharges(prev => updateChargeRecursively(prev, chargeId, 'update_field', updatePayload));
  }, []); // `updateChargeRecursively` is stable

  const handleToggleChargeExpansionInForm = useCallback((chargeId: string) => setAdditionalCharges(prev => updateChargeRecursively(prev, chargeId, 'toggle_expand_form', null)), []); // `updateChargeRecursively` is stable

  const handleToggleChargeExpansionInSummary = (chargeId: string) => setExpandedCustomChargesSummary(prev => ({ ...prev, [chargeId]: !prev[chargeId] }));

  // --- CORE CALCULATION LOGIC ---
  useEffect(() => {
    const { totalCallsPerMonth, avgCallDuration } = selections;
    const newTotalMinutes = (totalCallsPerMonth || 0) * (avgCallDuration || 0);
    const selectedCloud = config.cloud.find(c => c.id === selections.selectedCloudConfigId);
    const newCloudCost = selectedCloud ? (selectedCloud.billing === 'Monthly' ? selectedCloud.price : selectedCloud.price * (selections.cloudHoursPerMonth || 0)) : 0;
    const selectedStorage = config.storage.find(s => s.id === selections.selectedStorageConfigId);
    const newStorageCost = selectedStorage ? selectedStorage.price_per_GB * (selections.storageEstimatedGB || 0) : 0;
    const newDeploymentTotalCost = newCloudCost + newStorageCost;
    const selectedInbound = config.telephony.find(t => t.id === selections.selectedInboundTelephonyConfigId);
    const newInboundTelephonyCost = selectedInbound ? newTotalMinutes * selectedInbound.price_per_minute : 0;
    const selectedOutbound = config.telephony.find(t => t.id === selections.selectedOutboundTelephonyConfigId);
    const newOutboundTelephonyCost = selectedOutbound ? newTotalMinutes * selectedOutbound.price_per_minute : 0;
    const newTotalTelephonyCost = newInboundTelephonyCost + newOutboundTelephonyCost;
    const selectedTTS = config.tts.find(t => t.id === selections.selectedTTSConfigId);
    const newTTSCost = selectedTTS ? newTotalMinutes * selectedTTS.price_per_minute : 0;
    const selectedSTT = config.stt.find(s => s.id === selections.selectedSTTConfigId);
    const newSTTCost = selectedSTT ? newTotalMinutes * selectedSTT.price_per_minute : 0;
    const { llm } = config;
    const newLLMInputCost = newTotalMinutes * llm.input_tokens_per_minute * (llm.price_per_million_input / 1_000_000);
    const newLLMOutputCost = newTotalMinutes * llm.output_tokens_per_minute * (llm.price_per_million_output / 1_000_000);
    const newLLMTotalCost = newLLMInputCost + newLLMOutputCost;
    const newServicesTotalCost = newTTSCost + newSTTCost + newLLMTotalCost;
    const newAdditionalChargesCost = calculateRecursiveChargeCost(additionalCharges);
    const newTotalMonthlyCostUSD = newDeploymentTotalCost + newTotalTelephonyCost + newServicesTotalCost + newAdditionalChargesCost;

    setCosts({
      totalMinutes: newTotalMinutes, cloudCost: newCloudCost, storageCost: newStorageCost,
      deploymentTotalCost: newDeploymentTotalCost, inboundTelephonyCost: newInboundTelephonyCost,
      outboundTelephonyCost: newOutboundTelephonyCost, totalTelephonyCost: newTotalTelephonyCost,
      ttsCost: newTTSCost, sttCost: newSTTCost, llmInputCost: newLLMInputCost, llmOutputCost: newLLMOutputCost,
      llmTotalCost: newLLMTotalCost, servicesTotalCost: newServicesTotalCost,
      additionalChargesCost: newAdditionalChargesCost, totalMonthlyCostUSD: newTotalMonthlyCostUSD,
    });
  }, [selections, config, additionalCharges, calculateRecursiveChargeCost]);

  const formatCurrency = useCallback((amountUSD: number) => {
    const rate = exchangeRates[displayCurrency] || 1;
    return `${displayCurrency} ${ (amountUSD * rate).toFixed(2) }`;
  }, [displayCurrency]);

  const filterByCountry = <T extends { country?: string }>(items: T[], selectedCountry: string | null): T[] => {
    if (!selectedCountry || selectedCountry === "Global") return items.filter(item => !item.country || item.country === "Global");
    return items.filter(item => item.country === selectedCountry || !item.country);
  };

  const availableCloudOptions = useMemo(() => filterByCountry(config.cloud, selections.selectedCountry).map(c => ({ value: c.id, label: `${c.provider} - ${c.instance_type} (${c.region})` })), [config.cloud, selections.selectedCountry]);
  const availableStorageOptions = useMemo(() => filterByCountry(config.storage, selections.selectedCountry).map(s => ({ value: s.id, label: `${s.provider} (${s.region}, $${s.price_per_GB}/GB)` })), [config.storage, selections.selectedCountry]);
  const availableInboundTelephonyOptions = useMemo(() => filterByCountry(config.telephony.filter(t => t.direction === 'Inbound'), selections.selectedCountry).map(t => ({ value: t.id, label: `${t.provider} - ${t.region} ($${t.price_per_minute}/min)` })), [config.telephony, selections.selectedCountry]);
  const availableOutboundTelephonyOptions = useMemo(() => filterByCountry(config.telephony.filter(t => t.direction === 'Outbound'), selections.selectedCountry).map(t => ({ value: t.id, label: `${t.provider} - ${t.region} ($${t.price_per_minute}/min)` })), [config.telephony, selections.selectedCountry]);

  const availableTTSOptions = useMemo(() => config.tts.map(t => ({ value: t.id, label: `${t.provider} (${t.language}, $${t.price_per_minute}/min)` })), [config.tts]);
  const availableSTTOptions = useMemo(() => config.stt.map(s => ({ value: s.id, label: `${s.provider} (${s.language}, $${s.price_per_minute}/min)` })), [config.stt]);

  const selectedCloudDetails = useMemo(() => config.cloud.find(c => c.id === selections.selectedCloudConfigId), [config.cloud, selections.selectedCloudConfigId]);

  const pieData = useMemo(() => {
    const data = [
      { name: 'Deployment', value: costs.deploymentTotalCost },
      { name: 'Services', value: costs.servicesTotalCost },
      { name: 'Telephony', value: costs.totalTelephonyCost },
      ...(costs.additionalChargesCost > 0 ? [{ name: 'Additional', value: costs.additionalChargesCost }] : [])
    ];
    return data.filter(item => item.value > 0);
  }, [costs]);

  // --- PDF GENERATION LOGIC: handleDownloadPDF ---
  const handleDownloadPDF = async () => {
    setIsDownloadingPDF(true);

    const doc = new jsPDF('p', 'mm', 'a4');
    let yPos = 15; // Starting Y position for content
    const margin = 20; // Left/Right margin
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const lineHeight = 6; // Standard line height for text
    const sectionTitleSize = 18;
    const subSectionTitleSize = 14;
    const detailTextSize = 10;
    const totalCostSize = 24;
    const headerColor = '#333333';
    const accentColor = '#2563eb';
    const textColor = '#333333';
    const lightTextColor = '#6b7280';
    const tableHeaderBg = '#e0f2fe';
    const tableRowEvenBg = '#f8fafc';
    const tableRowOddBg = '#ffffff';
    const footerTextColor = '#9ca3af';

    // Helper function to check if a new page is needed
    const checkPageBreak = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = margin; // Reset yPos for new page
        // Add a simple header for continuity on new pages
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(accentColor);
        doc.text('Voice Bot Pricing Calculator - Continued', margin, 10);
        doc.setDrawColor(accentColor);
        doc.line(margin, 12, pageWidth - margin, 12);
        yPos = 20; // Adjust yPos after header
      }
    };

    // Helper to add section titles with consistent styling
    const addSectionTitle = (title: string, size: number, color: string = accentColor, bold: boolean = true) => {
      checkPageBreak(lineHeight * 2); // Ensure space for title and line
      doc.setFont("Helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      doc.setTextColor(color);
      doc.text(title, margin, yPos);
      doc.setDrawColor(accentColor);
      doc.setLineWidth(0.2);
      doc.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
      yPos += lineHeight * 1.8; // Move Y position down
    };

    // Helper to add key-value pairs, right-aligning the value
    const addKeyValue = (label: string, value: string, indent: number = 0, labelSize: number = detailTextSize, valueSize: number = detailTextSize, valueColor: string = textColor) => {
      checkPageBreak(lineHeight);
      doc.setFont("Helvetica", "normal");
      doc.setFontSize(labelSize);
      doc.setTextColor(lightTextColor);
      doc.text(label, margin + indent, yPos);

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(valueSize);
      doc.setTextColor(valueColor);
      doc.text(value, pageWidth - margin, yPos, { align: "right" });
      yPos += lineHeight;
    };

    // --- Page 1: Header and Summary ---
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(headerColor);
    doc.text('Voice Bot Pricing Calculator', margin, yPos);
    yPos += 10;
    doc.setFontSize(12);
    doc.setTextColor(lightTextColor);
    doc.text('Estimated Monthly Costs', margin, yPos);
    yPos += 20;

    addSectionTitle('Summary of Estimation', sectionTitleSize, accentColor);

    addKeyValue('Report Date', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
    addKeyValue('Display Currency', displayCurrency);
    addKeyValue('Operating Country', selections.selectedCountry || 'Not Selected');
    addKeyValue('Total Calls per Month', (selections.totalCallsPerMonth || 0).toLocaleString());
    addKeyValue('Avg. Call Duration', `${(selections.avgCallDuration || 0).toFixed(1)} minutes`);
    addKeyValue('Total Minutes per Month', `${costs.totalMinutes.toLocaleString()} minutes`);
    yPos += lineHeight * 2; // Add extra space before next section

    // --- Cost Breakdown Table Style ---
    addSectionTitle('Detailed Cost Breakdown', sectionTitleSize, accentColor);

    const tableHeaders = ["Category", "Cost"];
    const colWidths = [120, 50]; // Column widths in mm
    const startX = margin;
    let currentTableY = yPos; // Use a separate variable for table Y position to allow adding rows
    const rowHeight = lineHeight + 2; // Height of each table row including padding

    // Draw Table Headers
    checkPageBreak(rowHeight); // Ensure space for header row
    doc.setFillColor(tableHeaderBg);
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(detailTextSize);
    doc.setTextColor(headerColor);
    doc.rect(startX, currentTableY, colWidths[0], rowHeight, 'F'); // Draw rect for Category header
    doc.text(tableHeaders[0], startX + 5, currentTableY + lineHeight - 1);
    doc.rect(startX + colWidths[0], currentTableY, colWidths[1], rowHeight, 'F'); // Draw rect for Cost header
    doc.text(tableHeaders[1], startX + colWidths[0] + colWidths[1] - 5, currentTableY + lineHeight - 1, { align: "right" });
    currentTableY += rowHeight;

    // Helper to add a row to the detailed cost table
    const addTableRow = (category: string, cost: number, isSub: boolean = false, isCategoryTotal: boolean = false, isDeepSub: boolean = false) => {
      checkPageBreak(rowHeight + (isCategoryTotal ? 4 : 0)); // Add extra space if it's a category total for better separation
      const rowY = currentTableY;
      const fillColor = (currentTableY % (2 * rowHeight) === yPos + rowHeight) ? tableRowEvenBg : tableRowOddBg; // Alternate row colors
      doc.setFillColor(fillColor);
      doc.rect(startX, rowY, colWidths[0] + colWidths[1], rowHeight, 'F');

      doc.setFont("Helvetica", isCategoryTotal ? "bold" : "normal"); // Bold for category totals
      doc.setFontSize(detailTextSize + (isCategoryTotal ? 1 : 0)); // Slightly larger for category totals
      doc.setTextColor(isCategoryTotal ? accentColor : textColor); // Accent color for category totals

      let categoryIndent = startX + 5; // Default indent
      if (isSub) {
          categoryIndent = startX + 10; // Indent for direct sub-items
      }
      if (isDeepSub) { // Further indent for items like LLM Input/Output
          categoryIndent = startX + 15;
      }

      doc.text(category, categoryIndent, rowY + lineHeight - 1);

      doc.setFont("Helvetica", "bold"); // Always bold the cost value
      doc.setFontSize(detailTextSize + (isCategoryTotal ? 1 : 0));
      doc.setTextColor(isCategoryTotal ? accentColor : textColor);
      doc.text(formatCurrency(cost), startX + colWidths[0] + colWidths[1] - 5, rowY + lineHeight - 1, { align: "right" });
      currentTableY += rowHeight;
    };

    // Add cost categories to the table
    addTableRow('Deployment Costs', costs.deploymentTotalCost, false, true);
    if (costs.cloudCost > 0) addTableRow(`  Cloud (${selectedCloudDetails?.provider || 'N/A'})`, costs.cloudCost, true);
    if (costs.storageCost > 0) addTableRow(`  Storage`, costs.storageCost, true);

    addTableRow('Telephony Costs', costs.totalTelephonyCost, false, true);
    if (costs.inboundTelephonyCost > 0) addTableRow(`  Inbound Calls`, costs.inboundTelephonyCost, true);
    if (costs.outboundTelephonyCost > 0) addTableRow(`  Outbound Calls`, costs.outboundTelephonyCost, true);
    addTableRow(`  Total Minutes: ${costs.totalMinutes.toLocaleString()}`, 0, true); // Informative line, cost is 0

    addTableRow('Service Costs', costs.servicesTotalCost, false, true);
    if (costs.ttsCost > 0) addTableRow(`  TTS (Text-to-Speech)`, costs.ttsCost, true);
    if (costs.sttCost > 0) addTableRow(`  STT (Speech-to-Text)`, costs.sttCost, true);
    if (costs.llmTotalCost > 0) {
      addTableRow(`  LLM (Large Language Model)`, costs.llmTotalCost, true); // This is a sub-category itself
      addTableRow(`    - Input Tokens`, costs.llmInputCost, false, false, true); // Deep sub-item
      addTableRow(`    - Output Tokens`, costs.llmOutputCost, false, false, true); // Deep sub-item
    }

    // Additional Charges Section
    if (additionalCharges.length > 0) {
      currentTableY += lineHeight * 1.5; // Add some vertical space before the next section title
      yPos = currentTableY; // Align yPos for the new section title
      addSectionTitle('Additional Custom Charges', subSectionTitleSize, accentColor);

      // Recursive function for rendering additional charges in the PDF
      const renderAdditionalChargesForPDF = (charges: AdditionalCharge[], currentY: number, level: number): number => {
        let Y = currentY;
        const indentStep = 8; // Millimeters to indent for each level

        charges.forEach((charge) => {
          checkPageBreak(lineHeight * 2); // Check for space for the item and its potential sub-items

          const itemIndent = margin + (level * indentStep);
          const totalSubCost = charge.subCharges ? calculateRecursiveChargeCost(charge.subCharges) : 0;
          const totalItemCost = charge.price + totalSubCost; // Total cost for this charge including its sub-charges

          doc.setFont("Helvetica", level === 0 ? "bold" : "normal");
          doc.setFontSize(detailTextSize + (level === 0 ? 1 : 0)); // Main categories slightly larger
          doc.setTextColor(textColor);
          doc.text(`${'  '.repeat(level)}${charge.name}:`, itemIndent, Y); // Added spaces for visual indent

          doc.setFont("Helvetica", "bold");
          doc.setFontSize(detailTextSize + (level === 0 ? 1 : 0));
          doc.setTextColor(level === 0 ? accentColor : textColor); // Accent color for top-level custom charges
          doc.text(formatCurrency(totalItemCost), pageWidth - margin, Y, { align: "right" });
          Y += lineHeight;

          // If there's a direct price AND sub-items, explicitly show the direct price as a sub-detail.
          if (charge.price > 0 && charge.subCharges && charge.subCharges.length > 0) {
              checkPageBreak(lineHeight);
              doc.setFont("Helvetica", "normal");
              doc.setFontSize(detailTextSize - 1); // Smaller for base price detail
              doc.setTextColor(lightTextColor);
              doc.text(`(Base Cost):`, itemIndent + indentStep, Y); // Further indent for clarity
              doc.setFont("Helvetica", "normal");
              doc.setTextColor(textColor);
              doc.text(formatCurrency(charge.price), pageWidth - margin, Y, { align: "right" });
              Y += lineHeight;
          }

          if (charge.subCharges && charge.subCharges.length > 0) {
            // Optional: Add a small sub-header for items in a category for better structure
            if (level === 0) { // Only for top-level categories
                checkPageBreak(lineHeight);
                doc.setFont("Helvetica", "italic");
                doc.setFontSize(detailTextSize - 1);
                doc.setTextColor(lightTextColor);
                doc.text(`Items in '${charge.name}':`, itemIndent + indentStep, Y);
                Y += lineHeight;
            }
            Y = renderAdditionalChargesForPDF(charge.subCharges, Y, level + 1);
          }
        });
        return Y;
      };
      yPos = renderAdditionalChargesForPDF(additionalCharges, yPos, 0); // Start rendering from yPos
      currentTableY = yPos; // Sync currentTableY after rendering additional charges
    }

    currentTableY += lineHeight * 2; // Add some padding before the final total

    checkPageBreak(lineHeight * 3); // Ensure sufficient space for the total cost line
    yPos = currentTableY; // Ensure yPos is aligned for the total cost

    // Total Monthly Cost - Prominently displayed
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(totalCostSize);
    doc.setTextColor(accentColor);
    doc.text('Total Monthly Cost:', margin, yPos);
    doc.text(formatCurrency(costs.totalMonthlyCostUSD), pageWidth - margin, yPos, { align: "right" });
    yPos += lineHeight * 1.5;

    // --- Add Pie Chart as an Image ---
    if (chartContainerRef.current) {
      checkPageBreak(100); // Reserve a fixed space for the chart to ensure it fits
      const canvas = await html2canvas(chartContainerRef.current, {
        scale: 2, // Increase resolution for better PDF quality
        backgroundColor: null, // Transparent background
        useCORS: true, // If images are loaded from a different origin
      });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 80; // Fixed width for the image in PDF (e.g., 80mm)
      const imgHeight = (canvas.height * imgWidth) / canvas.width; // Maintain aspect ratio
      const imgX = (pageWidth - imgWidth) / 2; // Center the image horizontally

      checkPageBreak(imgHeight + 10); // Check again with calculated height + some padding
      doc.addImage(imgData, 'PNG', imgX, yPos, imgWidth, imgHeight);
      yPos += imgHeight + 10;
    }

    // --- Footer ---
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(footerTextColor);
    doc.text(`Generated by Voice Bot Pricing Calculator on ${new Date().toLocaleString()}`, margin, pageHeight - 10);

    doc.save('VoiceBot_Pricing_Estimate.pdf');
    setIsDownloadingPDF(false);
  };


  return (
    <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Product Estimation</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* --- INPUTS SECTION --- */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
          <SectionTitle title="Deployment Configuration" icon={<BarChart3 className="text-indigo-500" />} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <FormField label="Operating Country" name="selectedCountry" value={selections.selectedCountry} onChange={handleChange}
              type="select" options={config.countries.map(c => ({ value: c, label: c }))} />
            <FormField label="Cloud Instance" name="selectedCloudConfigId" value={selections.selectedCloudConfigId} onChange={handleChange}
              type="select" options={availableCloudOptions} />
            {selectedCloudDetails?.billing === 'Hourly' && (
              <FormField label="Cloud Hours per Month" name="cloudHoursPerMonth" value={selections.cloudHoursPerMonth} onChange={handleChange}
                type="number" min={0} step={1} unit="hours" />
            )}
            <FormField label="Storage Configuration" name="selectedStorageConfigId" value={selections.selectedStorageConfigId} onChange={handleChange}
              type="select" options={availableStorageOptions} />
            <FormField label="Estimated Storage Usage" name="storageEstimatedGB" value={selections.storageEstimatedGB} onChange={handleChange}
              type="number" min={0} step={1} unit="GB" />
          </div>

          <SectionTitle title="Call Volume & Services" icon={<Activity className="text-green-500" />} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <FormField label="Total Calls per Month" name="totalCallsPerMonth" value={selections.totalCallsPerMonth} onChange={handleChange}
              type="number" min={0} step={100} />
            <FormField label="Average Call Duration" name="avgCallDuration" value={selections.avgCallDuration} onChange={handleChange}
              type="number" min={0.1} step={0.1} unit="minutes" />
            <FormField label="Inbound Telephony" name="selectedInboundTelephonyConfigId" value={selections.selectedInboundTelephonyConfigId} onChange={handleChange}
              type="select" options={availableInboundTelephonyOptions} />
            <FormField label="Outbound Telephony" name="selectedOutboundTelephonyConfigId" value={selections.selectedOutboundTelephonyConfigId} onChange={handleChange}
              type="select" options={availableOutboundTelephonyOptions} />
            <FormField label="Text-to-Speech (TTS)" name="selectedTTSConfigId" value={selections.selectedTTSConfigId} onChange={handleChange}
              type="select" options={availableTTSOptions} />
            <FormField label="Speech-to-Text (STT)" name="selectedSTTConfigId" value={selections.selectedSTTConfigId} onChange={handleChange}
              type="select" options={availableSTTOptions} />
            {/* LLM details for reference, not selectable directly for simplicity */}
            <div className="col-span-full bg-blue-50 p-4 rounded-md text-sm text-blue-800 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-1 flex items-center"><Brain size={18} className="mr-2" /> LLM (Large Language Model) Pricing (Fixed):</h3>
              <p>Input: ${config.llm.price_per_million_input}/M tokens, Output: ${config.llm.price_per_million_output}/M tokens</p>
              <p>Estimated Usage: {config.llm.input_tokens_per_minute} input tokens/min, {config.llm.output_tokens_per_minute} output tokens/min</p>
            </div>
          </div>

          <SectionTitle title="Additional Monthly Charges" icon={<Coins className="text-purple-500" />} />
          <div className="mb-6">
            <button onClick={handleAddTopLevelCharge} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md flex items-center">
              <PlusCircle size={20} className="mr-2" /> Add New Category
            </button>
            <div className="mt-4 space-y-4">
              {additionalCharges.map(charge => (
                <RenderAdditionalCharge
                  key={charge.id}
                  charge={charge}
                  level={0}
                  onUpdate={handleUpdateChargeField}
                  onAddSub={handleAddSubCharge}
                  onRemove={handleRemoveCharge}
                  onToggleExpand={handleToggleChargeExpansionInForm}
                />
              ))}
            </div>
          </div>
        </div>

        {/* --- SUMMARY SECTION --- */}
        <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-md sticky top-6 self-start">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
            <BarChart3 className="text-indigo-500 mr-3" /> Cost Summary
          </h2>

          <div className="mb-4">
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">Display Currency:</label>
            <select id="currency" name="currency" value={displayCurrency} onChange={(e) => setDisplayCurrency(e.target.value as Currency)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5 bg-white text-gray-900">
              {Object.keys(exchangeRates).map(currencyCode => (
                <option key={currencyCode} value={currencyCode}>{currencyCode}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Overall Breakdown:</h3>
            <div ref={chartContainerRef} className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border-t pt-4">
            <ExpandableSummaryItem
              label="Deployment Costs"
              valueText={formatCurrency(costs.deploymentTotalCost)}
              isExpanded={isDeploymentExpanded}
              onToggle={() => setIsDeploymentExpanded(!isDeploymentExpanded)}
            >
              {costs.cloudCost > 0 && selectedCloudDetails && (
                <SummaryItem
                  label={`Cloud (${selectedCloudDetails.provider} - ${selectedCloudDetails.instance_type})`}
                  valueText={formatCurrency(costs.cloudCost)}
                  isSubItem
                />
              )}
              {costs.storageCost > 0 && (
                <SummaryItem
                  label="Storage"
                  valueText={formatCurrency(costs.storageCost)}
                  isSubItem
                />
              )}
            </ExpandableSummaryItem>

            <ExpandableSummaryItem
              label="Telephony Costs"
              valueText={formatCurrency(costs.totalTelephonyCost)}
              isExpanded={isTelephonyExpanded}
              onToggle={() => setIsTelephonyExpanded(!isTelephonyExpanded)}
            >
              <SummaryItem label={`Total Call Minutes: ${costs.totalMinutes.toLocaleString()}`} valueText="" isSubItem />
              {costs.inboundTelephonyCost > 0 && (
                <SummaryItem label="Inbound Calls" valueText={formatCurrency(costs.inboundTelephonyCost)} isSubItem />
              )}
              {costs.outboundTelephonyCost > 0 && (
                <SummaryItem label="Outbound Calls" valueText={formatCurrency(costs.outboundTelephonyCost)} isSubItem />
              )}
            </ExpandableSummaryItem>

            <ExpandableSummaryItem
              label="Service Costs (TTS, STT, LLM)"
              valueText={formatCurrency(costs.servicesTotalCost)}
              isExpanded={isServicesExpanded}
              onToggle={() => setIsServicesExpanded(!isServicesExpanded)}
            >
              {costs.ttsCost > 0 && (
                <SummaryItem label="Text-to-Speech (TTS)" valueText={formatCurrency(costs.ttsCost)} isSubItem />
              )}
              {costs.sttCost > 0 && (
                <SummaryItem label="Speech-to-Text (STT)" valueText={formatCurrency(costs.sttCost)} isSubItem />
              )}
              {costs.llmTotalCost > 0 && (
                <>
                  <SummaryItem label="LLM (Large Language Model)" valueText={formatCurrency(costs.llmTotalCost)} isSubItem />
                  <SummaryItem label="  - Input Tokens" valueText={formatCurrency(costs.llmInputCost)} isSubItem />
                  <SummaryItem label="  - Output Tokens" valueText={formatCurrency(costs.llmOutputCost)} isSubItem />
                </>
              )}
            </ExpandableSummaryItem>

            {costs.additionalChargesCost > 0 && (
              <ExpandableSummaryItem
                label="Additional Charges"
                valueText={formatCurrency(costs.additionalChargesCost)}
                isExpanded={isAdditionalChargesExpanded}
                onToggle={() => setIsAdditionalChargesExpanded(!isAdditionalChargesExpanded)}
              >
                {/* Recursive rendering for additional charges in summary */}
                {additionalCharges.map(charge => {
                  const renderNestedCharges = (nestedCharge: AdditionalCharge, level: number) => (
                    <React.Fragment key={nestedCharge.id}>
                      <SummaryItem
                        label={`${'--'.repeat(level)} ${nestedCharge.name}`}
                        valueText={formatCurrency(nestedCharge.price + (nestedCharge.subCharges ? calculateRecursiveChargeCost(nestedCharge.subCharges) : 0))}
                        isSubItem={level > 0}
                      />
                      {nestedCharge.subCharges && nestedCharge.subCharges.length > 0 &&
                        expandedCustomChargesSummary[nestedCharge.id] && // Only show sub-items if expanded
                        <div className="pl-4">
                          {nestedCharge.subCharges.map(sub => renderNestedCharges(sub, level + 1))}
                        </div>
                      }
                    </React.Fragment>
                  );
                  return renderNestedCharges(charge, 0);
                })}
              </ExpandableSummaryItem>
            )}

            <div className="flex justify-between py-3 mt-4 border-t-2 border-indigo-200">
              <span className="text-xl font-bold text-gray-800">Total Monthly Cost:</span>
              <span className="text-2xl font-extrabold text-indigo-700">{formatCurrency(costs.totalMonthlyCostUSD)}</span>
            </div>

            <button
              onClick={handleDownloadPDF}
              disabled={isDownloadingPDF}
              className={`w-full mt-6 py-3 px-6 rounded-md text-white font-semibold flex items-center justify-center transition-colors duration-200
              ${isDownloadingPDF ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              {isDownloadingPDF ? (
                <>
                  <Loader size={20} className="animate-spin mr-2" /> Generating PDF...
                </>
              ) : (
                <>
                  <Download size={20} className="mr-2" /> Download PDF Estimate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;